import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    portalColors,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalFormGroup,
    StandardModalInput,
    StandardModalLabel,
    StandardModalTableSection,
    StandardModalTableScroll
} from "src/components";
import { UserKeys } from "src/constants/userDetails";
import { UserContext } from "src/scenes/App/UserContext";
import { useLeaveChat } from "src/scenes/Chat/hooks";
import { capitalizeString, trimStringWithEllipsis } from "src/utils/helpers";
import UserAvatar from "./UserAvatar";

const ContentStack = styled.div`
    display: grid;
    gap: 1rem;
`;

const ReadOnlyInput = styled(StandardModalInput)`
    background: ${portalColors.surface};
    color: ${portalColors.text};
    cursor: default;
`;

const MembersList = styled(StandardModalTableSection)`
    background: ${portalColors.surface};
`;

const AvatarCell = styled.div`
    display: flex;
    align-items: center;
    padding-top: 0.625rem;
`;

const MemberRow = styled.button`
    width: 100%;
    min-height: 4.75rem;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.9rem;
    padding: 0.75rem 1rem;
    box-sizing: border-box;
    border: 0;
    border-bottom: 1px solid ${portalColors.border};
    background: transparent;
    color: ${portalColors.text};
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    text-align: left;

    &:last-child {
        border-bottom: 0;
    }

    &:hover,
    &:focus-visible {
        background: ${portalColors.surfaceSoft};
        outline: none;
    }

    @media (max-width: 640px) {
        grid-template-columns: auto minmax(0, 1fr);
        align-items: flex-start;
    }
`;

const MemberName = styled.div`
    min-width: 0;
    color: ${portalColors.textSoft};
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

const MemberBadges = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-wrap: wrap;

    @media (max-width: 640px) {
        grid-column: 2;
        justify-content: flex-start;
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 1.75rem;
    padding: 0 0.65rem;
    border-radius: 999px;
    background: ${portalColors.blueSoft};
    border: 1px solid ${portalColors.borderStrong};
    color: ${portalColors.blue};
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
`;

const ViewGroupChatModal = (props) => {
    const { showDialog, handleClose, currentChat } = props;
    const currentUser = useContext(UserContext);
    const { isLeavingChat, leaveChat, requestToLeaveChat } = useLeaveChat();
    const currentUserUuid = currentUser[UserKeys.UUID];
    const activeMembers = useMemo(() => {
        if (!currentChat) {
            return [];
        }

        return currentChat.users.filter(user => user.currentMember);
    }, [currentChat]);
    const currentChatUser = activeMembers.find(user => user.employeeUuid === currentUserUuid);
    const isCurrentUserOwner = currentChat?.ownerUuid === currentUserUuid;
    const [isRequestingRemoval, setIsRequestingRemoval] = useState(false);

    useEffect(() => {
        setIsRequestingRemoval(Boolean(currentChatUser?.requestingLeave));
    }, [currentChatUser?.requestingLeave]);

    const handleLeaveGroup = async () => {
        await leaveChat(currentChat, undefined);
        handleClose();
    };

    const handleRequestRemoval = async () => {
        const requestingLeave = !isRequestingRemoval;
        await requestToLeaveChat(currentChat.uuid, requestingLeave);
        currentChatUser.requestingLeave = requestingLeave;
        setIsRequestingRemoval(requestingLeave);
    };

    const renderUsers = () => {
        if (!currentChat) {
            return null;
        }

        return activeMembers.map((user) => {
            const userEmployeeDetailsFullName = trimStringWithEllipsis(
                `${capitalizeString(user.employeeDetails.firstName)} ${capitalizeString(user.employeeDetails.lastName)}`
            );
            const isOwner = currentChat.ownerUuid === user.employeeUuid;

            return (
                <MemberRow
                    key={`user-list-item-${user.employeeUuid}`}
                    type="button"
                    onClick={() => props.displayOtherUserProfileModal(user)}
                >
                    <AvatarCell>
                        <UserAvatar
                            width="3.125rem"
                            height="3.125rem"
                            hideInfo={true}
                            photo={user.employeeDetails.profilePictureUrl}
                            background="#D6D6D6"
                            firstName={user.employeeDetails.firstName}
                            lastName={user.employeeDetails.lastName}
                            statusBorder="1px solid #fff"
                            hideStatus={true}
                            withShadow={true}
                            pixelBorder="1px"
                        />
                    </AvatarCell>
                    <MemberName>{userEmployeeDetailsFullName}</MemberName>
                    <MemberBadges>
                        {isOwner && <StatusBadge>Group Admin</StatusBadge>}
                    </MemberBadges>
                </MemberRow>
            );
        });
    };

    return (
        <StandardModal
            open={showDialog}
            onClose={handleClose}
            title="Group Details"
            widthMode="large"
            footer={
                <>
                    {!isCurrentUserOwner && currentChatUser && (
                        <StandardModalActionButton
                            $variant={currentChat.allowUsersToLeave ? "danger" : undefined}
                            minWidth="11.5rem"
                            disabled={isLeavingChat}
                            onClick={currentChat.allowUsersToLeave ? handleLeaveGroup : handleRequestRemoval}
                        >
                            {currentChat.allowUsersToLeave
                                ? "Leave Group"
                                : isRequestingRemoval ? "Cancel Removal Request" : "Request Removal"}
                        </StandardModalActionButton>
                    )}
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton onClick={handleClose}>
                            Close
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </>
            }
        >
            {currentChat && (
                <ContentStack>
                    <StandardModalFormGroup>
                        <StandardModalLabel>Group Name</StandardModalLabel>
                        <ReadOnlyInput value={currentChat.name} readOnly />
                    </StandardModalFormGroup>
                    <StandardModalFormGroup>
                        <StandardModalLabel>Group Members</StandardModalLabel>
                        <MembersList>
                            <StandardModalTableScroll maxHeight="22rem">
                                {renderUsers()}
                            </StandardModalTableScroll>
                        </MembersList>
                    </StandardModalFormGroup>
                </ContentStack>
            )}
        </StandardModal>

    );
};

export default ViewGroupChatModal;
